import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AppButton, appButtonClassName } from '../../components/ui/AppButton';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { api, settingsApi } from '../../lib/api';
import { PLAY_BANNER_OFFSET_PADDING_CLASS } from '../../lib/layoutChrome';

export const AccountRemovalConfirmPage = () => {
  const { t } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'fail'>(
    token ? 'idle' : 'fail',
  );

  const handleConfirm = async () => {
    if (!token || status === 'loading') {
      return;
    }
    setStatus('loading');
    try {
      const response = await settingsApi.confirmAccountRemoval({ token });
      if (!response.success) {
        throw new Error(response.error ?? t('auth.accountRemovalConfirmFail'));
      }
      if (isAuthenticated) {
        try {
          await api.get('/auth/me');
        } catch {
          await logout();
        }
      }
      setStatus('ok');
    } catch {
      setStatus('fail');
    }
  };

  return (
    <div className={`flex min-h-screen items-center justify-center bg-gray-50 px-4 py-10 ${PLAY_BANNER_OFFSET_PADDING_CLASS}`}>
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
        <h1 className="text-2xl font-bold text-gray-900">{t('auth.accountRemovalConfirmTitle')}</h1>
        <div className="mt-6" role="status" aria-live="polite">
          {status === 'idle' ? (
            <p className="text-gray-600">{t('auth.accountRemovalConfirmPrompt')}</p>
          ) : null}
          {status === 'loading' ? (
            <p className="text-gray-600">{t('auth.accountRemovalConfirmPending')}</p>
          ) : null}
          {status === 'ok' ? (
            <p className="text-green-700">{t('auth.accountRemovalConfirmSuccess')}</p>
          ) : null}
          {status === 'fail' ? (
            <p className="text-red-700">
              {token ? t('auth.accountRemovalConfirmFail') : t('auth.missingToken')}
            </p>
          ) : null}
        </div>
        {status === 'idle' ? (
          <AppButton
            type="button"
            variant="danger"
            fullWidth
            className="mt-6 shadow-sm"
            onClick={() => void handleConfirm()}
            aria-label={t('auth.accountRemovalConfirmButton')}
          >
            {t('auth.accountRemovalConfirmButton')}
          </AppButton>
        ) : null}
        {(status === 'ok' || status === 'fail') ? (
          <Link
            to="/"
            className={appButtonClassName({ variant: 'primary', className: 'mt-6 shadow-sm' })}
          >
            {t('auth.goToHome')}
          </Link>
        ) : null}
      </div>
    </div>
  );
};
